import React, { useRef } from 'react';
import {
  Menu,
  MenuButton,
  MenuList,
  MenuOptionGroup,
  MenuItemOption,
  Button,
  Box,
  Portal,
  useDisclosure,
  ChevronDownIcon,
  HbTooltip,
} from '@cdl/heartbeat-react';
import { useDispatch, useSelector } from 'react-redux';
import {
  leadCountSelector,
  leadNamesSelector,
} from '../../ecg/selectors/signal.selectors';
import { usePlot } from '../contexts/plot.context';
import { useStrip } from '../hooks/useStrip';
import { navigateLead } from '../thunks/navigate-lead.thunk';
import { squareToPixel } from '../utils/graph.utils';
import { parseLeadName } from '../utils/lead';

const WIDTH = '6rem';

interface Props {
  leadCountThreshold?: number;
}

export const LeadPickerContainer: React.FC<Props> = ({
  leadCountThreshold = 3,
}) => {
  const dispatch = useDispatch();
  const ref = useRef<HTMLSpanElement>(null);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const leadCount = useSelector(leadCountSelector);
  const { stripId, leads, height, width } = useStrip();
  const { plotName } = usePlot();
  const leadNames = useSelector(leadNamesSelector);

  const handleTooltip = () => {
    if (
      ref.current !== null &&
      ref.current.offsetWidth < ref.current.scrollWidth
    ) {
      onOpen();
    }
  };

  return (
    <foreignObject
      width={width}
      height={squareToPixel(2) * leads.length}
      y={height / 2}
      overflow='visible'
    >
      <Box pl='xs' width='5.2rem'>
        <Menu>
          <HbTooltip
            label={parseLeadName(leads[0])}
            placement='right'
            hasArrow
            isOpen={isOpen}
          >
            <MenuButton
              as={Button}
              {...(leadCount > leadCountThreshold && {
                rightIcon: <ChevronDownIcon />,
              })}
              variant='secondary'
              textAlign='left'
              fontWeight='regular'
              width='100%'
              pr='.4rem'
              display='flex'
              isDisabled={leadCount <= leadCountThreshold}
              onClick={onClose}
              onPointerEnter={handleTooltip}
              onPointerLeave={onClose}
            >
              <Box
                as='span'
                width='100%'
                display='inline-block'
                overflow='hidden'
                whiteSpace='nowrap'
                textOverflow='ellipsis'
                ref={ref}
              >
                {parseLeadName(leads[0])}
              </Box>
            </MenuButton>
          </HbTooltip>
          <Portal>
            <MenuList minWidth='unset' width={WIDTH}>
              <MenuOptionGroup
                type='radio'
                value={leads[0]}
                onChange={(lead) =>
                  dispatch(
                    navigateLead(stripId, plotName, leads[0], lead as string),
                  )
                }
              >
                {leadNames.map((lead) => (
                  <MenuItemOption key={lead} value={lead}>
                    {parseLeadName(lead)}
                  </MenuItemOption>
                ))}
              </MenuOptionGroup>
            </MenuList>
          </Portal>
        </Menu>
      </Box>
    </foreignObject>
  );
};
